/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Chip, CircularProgress, TextField, InputAdornment,
  Button, Typography, Table, TableBody, TableCell, TableRow, Alert,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../api';
import { DROPDOWN_SX } from '../opdAppointments/constants';
import { useListUrlState } from '../../hooks/useListUrlState';
import BillQuickActionsMenu from '../opdBilling/BillQuickActionsMenu';

const toISO = (d) => d.toISOString().split('T')[0];
const defaultTo = toISO(new Date());
const defaultFrom = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toISO(d); })();

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';
// Both source APIs are fetched in full for the selected date range and merged
// client-side (the two domains are genuinely separate microservices/tables —
// see the OPD billing plan's "Accounts section" note), then paginated in the
// browser. This cap keeps a single fetch cheap; if a clinic's date range
// regularly exceeds it, this page needs real server-side cross-department
// pagination instead.
const FETCH_CAP = 300;
const PER_PAGE = 20;

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MODE_STYLE = {
  CASH:    { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', dot: '#43a047' },
  UPI:     { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', dot: '#1e88e5' },
  CARD:    { bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8', dot: '#8e24aa' },
  ONLINE:  { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', dot: '#1e88e5' },
  CHEQUE:  { bg: '#fce4ec', color: '#ad1457', border: '#f48fb1', dot: '#d81b60' },
  NEFT:    { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', dot: '#1e88e5' },
  RTGS:    { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', dot: '#1e88e5' },
};

const DEPT_STYLE = {
  PHARMACY: { bg: '#ede7f6', color: '#4527a0', icon: LocalPharmacyIcon },
  OPD:      { bg: '#e0f4f7', color: TEAL_DARK, icon: MedicalServicesIcon },
};

function StatCard({ icon, label, value, accent, sub }) {
  return (
    <Box sx={{
      flex: '1 1 160px', minWidth: 140, borderRadius: 3, background: '#fff',
      border: `1.5px solid ${accent}33`, boxShadow: `0 2px 12px ${accent}18`,
      px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.8,
    }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: `linear-gradient(135deg, ${accent}22, ${accent}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color: accent, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a202c', lineHeight: 1.4, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd', mt: 0.2 }}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

function ModeBadge({ mode }) {
  const s = MODE_STYLE[mode] || { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0', dot: '#bdbdbd' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.35, borderRadius: '20px', background: s.bg, border: `1px solid ${s.border}` }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{mode}</Typography>
    </Box>
  );
}

function DirectionBadge({ direction }) {
  const isIn = direction === 'IN';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: '20px', background: isIn ? '#e8f5e9' : '#fdecea', border: `1px solid ${isIn ? '#a5d6a7' : '#ef9a9a'}` }}>
      {isIn ? <ArrowDownwardIcon sx={{ fontSize: 11, color: '#2e7d32' }} /> : <ArrowUpwardIcon sx={{ fontSize: 11, color: '#c62828' }} />}
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: isIn ? '#2e7d32' : '#c62828', lineHeight: 1 }}>{isIn ? 'IN' : 'OUT'}</Typography>
    </Box>
  );
}

function DeptBadge({ department }) {
  const s = DEPT_STYLE[department] || { bg: '#f5f5f5', color: '#757575', icon: PaymentIcon };
  const Icon = s.icon;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '20px', background: s.bg }}>
      <Icon sx={{ fontSize: 12, color: s.color }} />
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{department}</Typography>
    </Box>
  );
}

function opdContactName(contactInfo, contactId) {
  if (!contactInfo) return `Patient #${contactId}`;
  return [contactInfo.first_name, contactInfo.middle_name, contactInfo.last_name].filter(Boolean).join(' ') || `Patient #${contactId}`;
}

function opdContactMobile(contactInfo) {
  return contactInfo?.phones?.find(p => p.is_default === 'Y')?.phone_number || contactInfo?.phones?.[0]?.phone_number || null;
}

/** Pharmacy's irm_pharm_bill_payments/returns/GRN payments — already normalized server-side. */
function normalizePharmacyRows(rows) {
  return (rows || []).map(r => ({
    key: `pharmacy-${r.type}-${r.id}`,
    department: 'PHARMACY',
    type: r.type,
    direction: r.direction,
    date: r.date,
    party: r.patient_name || '—',
    mobile: r.patient_mobile,
    doc_no: r.bill_no,
    mode: r.mode,
    reference_no: r.reference_no,
    amount: r.amount,
    status: r.status,
    route: r.bill_id ? (r.source === 'INWARD' ? `/pharmacy/inward/${r.bill_id}` : `/pharmacy/billing/${r.bill_id}`) : null,
  }));
}

/** OPD bills carry their payments/refunds nested — flatten them into the same shape here. */
function normalizeOpdRows(bills) {
  const rows = [];
  (bills || []).forEach(bill => {
    const party = opdContactName(bill.contactInfo, bill.irm_contacts_id);
    const mobile = opdContactMobile(bill.contactInfo);
    (bill.payments || []).filter(p => p.status === 'PAID').forEach(p => {
      rows.push({
        key: `opd-payment-${p.id}`,
        department: 'OPD',
        type: 'PAYMENT',
        direction: 'IN',
        date: p.paid_at,
        party,
        mobile,
        doc_no: bill.bill_no,
        mode: p.payment_mode,
        reference_no: p.reference_no,
        amount: p.amount,
        status: p.status,
        route: `/opd/billing/${bill.id}`,
        billId: bill.id,
      });
    });
    (bill.refunds || []).forEach(r => {
      rows.push({
        key: `opd-refund-${r.id}`,
        department: 'OPD',
        type: 'REFUND',
        direction: 'OUT',
        date: r.refunded_at,
        party,
        mobile,
        doc_no: bill.bill_no,
        mode: r.refund_method,
        reference_no: null,
        amount: r.amount,
        status: r.refund_type,
        route: `/opd/billing/${bill.id}`,
        billId: bill.id,
      });
    });
  });
  return rows;
}

export default function AccountsPaymentsPage() {
  const navigate = useNavigate();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [department, setDepartment] = useState(urlParams?.get('department') || '');
  const [fromDate, setFromDate] = useState(urlParams?.get('from_date') || defaultFrom);
  const [toDate, setToDate] = useState(urlParams?.get('to_date') || defaultTo);
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);

  const load = useCallback(async (fd, td) => {
    setLoading(true); setError('');
    try {
      const [pharmacyRes, opdRes] = await Promise.all([
        api.pharmacy.listCashLedger({ from_date: fd, to_date: td, per_page: FETCH_CAP, page: 1 }),
        api.opdBilling.listBills({ start_date: fd, end_date: td, limit: FETCH_CAP, offset: 0 }),
      ]);
      const merged = [
        ...normalizePharmacyRows(pharmacyRes?.rows),
        ...normalizeOpdRows(opdRes?.data),
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setAllRows(merged);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load payments');
      setAllRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(fromDate, toDate); }, [load]); // eslint-disable-line

  // Search/department/page are filtered client-side (no re-fetch), so keep
  // the URL in sync with every filter/page change here rather than inside load().
  useEffect(() => {
    writeParams({ from_date: fromDate, to_date: toDate, search, department, page });
  }, [fromDate, toDate, search, department, page, writeParams]);

  const handleDateFilter = (fd, td) => { setFromDate(fd); setToDate(td); setPage(1); load(fd, td); };

  const filtered = allRows.filter(r => {
    if (department && r.department !== department) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${r.party || ''} ${r.mobile || ''} ${r.doc_no || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const total = filtered.length;
  const totalPages = Math.ceil(total / PER_PAGE);
  const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const totalIn = pageRows.filter(r => r.direction === 'IN').reduce((s, r) => s + Number(r.amount), 0);
  const totalOut = pageRows.filter(r => r.direction === 'OUT').reduce((s, r) => s + Number(r.amount), 0);
  const net = totalIn - totalOut;

  const COL_HEADERS = ['Date', 'Dept', 'Type', 'Party', 'Contact', 'Ref Doc', 'Mode', 'Reference', 'Amount', 'Status', ''];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={0}>
        <Box sx={{
          borderRadius: 4, background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #003d4d 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 2,
          boxShadow: `0 8px 32px ${TEAL}44`, position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <PaymentIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ zIndex: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Accounts</Typography>
            <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>All Payments</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', mt: 0.3 }}>Pharmacy + OPD, in one place</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard icon={<TrendingUpIcon />} label="Total In" value={fmt(totalIn)} accent="#2e7d32" sub="payments received" />
          <StatCard icon={<TrendingDownIcon />} label="Total Out" value={fmt(totalOut)} accent="#c62828" sub="returns / refunds" />
          <StatCard icon={<AccountBalanceWalletIcon />} label="Net (this page)" value={fmt(net)} accent={TEAL} sub={`${pageRows.length} of ${total} records`} />
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{error}</Alert>}

        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
            <TextField
              size="small" placeholder="Search patient/party, mobile or doc no…"
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem', background: '#fff' } }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Department</InputLabel>
              <Select value={department} label="Department" onChange={e => { setDepartment(e.target.value); setPage(1); }} sx={DROPDOWN_SX}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PHARMACY">Pharmacy</MenuItem>
                <MenuItem value="OPD">OPD</MenuItem>
              </Select>
            </FormControl>
            <MDDatePicker label="From" value={fromDate} onChange={val => handleDateFilter(val || '', toDate)} sx={{ width: 150 }} />
            <MDDatePicker label="To" value={toDate} onChange={val => handleDateFilter(fromDate, val || '')} sx={{ width: 150 }} />
            {(fromDate !== defaultFrom || toDate !== defaultTo) && (
              <Chip label="Reset" size="small" clickable onClick={() => handleDateFilter(defaultFrom, defaultTo)}
                sx={{ fontSize: '0.65rem', height: 26, color: '#ef5350', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '20px' }} />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: TEAL }} size={36} />
              <Typography sx={{ fontSize: '0.8rem', color: '#b0bec5' }}>Loading payments…</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 950 }}>
                <TableBody>
                  <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                    {COL_HEADERS.map(h => (
                      <TableCell key={h} sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase', letterSpacing: 0.7, py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none', '&:first-of-type': { pl: 2.5 }, '&:last-of-type': { pr: 2.5 } }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>

                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COL_HEADERS.length}>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <PaymentIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No payments found</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', mt: 0.5 }}>Try adjusting your search, department or date range</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : pageRows.map((r, i) => {
                    const isIn = r.direction === 'IN';
                    return (
                      <TableRow key={r.key} sx={{
                        background: i % 2 === 0 ? '#fff' : '#fafcff',
                        borderLeft: isIn ? '3px solid #43a047' : '3px solid #e53935',
                        '&:hover': { background: TEAL_LIGHT },
                      }}>
                        <TableCell sx={{ py: 1.5, pl: 2.5 }}>
                          <Typography sx={{ fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}><DeptBadge department={r.department} /></TableCell>
                        <TableCell sx={{ py: 1.5 }}><DirectionBadge direction={r.direction} /></TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>{r.party}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#78909c' }}>{r.mobile || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {r.route ? (
                            <Button
                              size="small" endIcon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                              onClick={() => navigate(r.route)}
                              sx={{ fontSize: '0.73rem', color: TEAL, p: 0, minWidth: 0, fontFamily: 'monospace', fontWeight: 700, '&:hover': { color: TEAL_DARK, background: 'transparent', textDecoration: 'underline' } }}>
                              {r.doc_no || '—'}
                            </Button>
                          ) : <Typography sx={{ fontSize: '0.75rem', color: '#ccc' }}>{r.doc_no || '—'}</Typography>}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}><ModeBadge mode={r.mode} /></TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#90a4ae' }}>{r.reference_no || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.88rem', fontFamily: 'monospace', fontWeight: 800, color: isIn ? '#2e7d32' : '#c62828', whiteSpace: 'nowrap' }}>
                            {isIn ? '' : '−'}{fmt(r.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip label={r.status} size="small" sx={{ fontSize: '0.62rem', fontWeight: 700, height: 20, color: '#2e7d32', background: '#e8f5e9' }} />
                        </TableCell>
                        <TableCell sx={{ py: 1.5, pr: 2.5 }}>
                          {r.department === 'OPD' && r.billId && (
                            <BillQuickActionsMenu billId={r.billId} onSuccess={() => load(fromDate, toDate)} onView={() => navigate(r.route)} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.75, borderTop: '1px solid #f0f4f8', background: '#fafcff' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', fontWeight: 500 }}>
                Showing <b style={{ color: '#546e7a' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b style={{ color: '#546e7a' }}>{total}</b> results
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  sx={{ minWidth: 0, border: '1.5px solid #e2e8f0', borderRadius: 2, color: page <= 1 ? '#d0d0d0' : TEAL, p: 0.5 }}>
                  <KeyboardArrowLeftIcon fontSize="small" />
                </Button>
                <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  sx={{ minWidth: 0, border: '1.5px solid #e2e8f0', borderRadius: 2, color: page >= totalPages ? '#d0d0d0' : TEAL, p: 0.5 }}>
                  <KeyboardArrowRightIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
